import React, { useCallback, useEffect } from "react";
import styles from "./Offers.module.scss";
import CustomHeader from "@/components/CustomHeader/CustomHeader";
import Table from "@/components/Table/Table";
import { useDispatch, useSelector } from "react-redux";
import { getOffers } from "@/store/slices/offerSlice";
import { setModalOpen } from "@/store/slices/globalSlice";
import { MODAL_ID, OFFER_STATUS} from "@/lib/constants";
import AddOffer from "./components/AddOffer/AddOffer";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal/ConfirmDeleteModal";
import OfferService from "@/services/api/offer";
import toast from "react-hot-toast";
import { useSearchPagination } from "@/hooks/useSearchPagination";
import ActionsCell from "@/components/ActionCells/ActionsCell";
import { addBaseUrl, capitalizeText, checkPermission, conditionalColumns, dateConverter, updateParams } from "@/lib/utils";
import { useSearchParams } from "react-router-dom";
import RejectionModal from "@/components/RejectionModal/RejectionModal";

const tabNames = OFFER_STATUS.map((status) => capitalizeText(status));
const Offers = () => {
  const {
    debouncedSearch,
    search,
    setSearch,
    pageSelected,
    setPageSelected,
    rowsPerPageValue,
    setRowsPerPageValue,
  } = useSearchPagination();

  const dispatch = useDispatch();
  const [params,setParams] = useSearchParams()
  const tab = params.get("tab") ?? "0";
  const { isFetching, offers, totalOffers } = useSelector(
    (state) => state.offer
  );
  const { modalOpen } = useSelector((state) => state.global);
  const user = JSON.parse(localStorage.getItem("user"));
  const isVendor = user?.role === "vendor";
  console.log(isVendor);

  const canManageOffers =
    (isVendor && tab === "0") ||
    checkPermission("update user") ||
    checkPermission("delete user");

  const updateOfferStatus = (id, statusIndex) => async (reason) => {
    const payload = {
      approvalStatus: OFFER_STATUS[statusIndex],
    };
    if (reason && typeof reason === "string") {
      payload.rejectReason = reason;
    }
    const data = await OfferService.updateOfferStatus(id, payload);
    if (data.success) {
      toast.success(data?.message || "Offer status updated successfully");
      updateParams({ tab: statusIndex }, setParams);
    }
  };
  const actionsCellCallback = (row) => {
    if (isVendor && tab !== "0") return null;
    const pendingActions = {
          onApprove: updateOfferStatus(row.id, 1),
          onReject: () => {
            dispatch(setModalOpen({ id: MODAL_ID.REJECTION_MODAL, data: row.id }));
          },
        };
        return(
    <ActionsCell
      {...(tab === "0" ? pendingActions : {})}
      onEdit={checkPermission("update user") && (() => {
        dispatch(setModalOpen({ id: MODAL_ID.ADD_OFFER, data: row }));
      })}
     onDelete={checkPermission("delete user") && (() => {
  dispatch(setModalOpen({
    id: MODAL_ID.CONFIRM_DELETE,
    data: {
      id: row.id,
      userName: `"${row.name}"`, 
    },
  }));
})}

    />)
};

    const viewCellCallback = (row) => {
   const firstImagePath = row?.imagePath;


    if (!firstImagePath) return <span>No Image</span>;
    const imageUrl = addBaseUrl(firstImagePath.replace("public/", ""));
    return (
   
        <img
          src={imageUrl}
          alt="product"
          className={styles.imageIcon}
          onClick={() => {
            dispatch(setModalOpen({ id: MODAL_ID.VIEW_IMAGE, data: row }));
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "/fallback_product_image.jpg";
          }}
        />
     
    );
  };

  const columns = [
    {
      name: "Images",
      width: "8%",
      cell: viewCellCallback,
    },
    {
      name: "Offer Name",
      width: "14%",
      selector: (row) => row.name,
      cell: (row) => (
        <div
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "150px",
            cursor: "pointer",
          }}
          title={row.name}
        >
          {row.name}
        </div>
      ),
    },
    {
      name: "Offer Type",
      width: "10%",
      selector: (row) => row.type,
    },
    {
      name: "Discount Type",
      width: "10%",
      selector: (row) => row.discountType,
    },
    {
      name: "Discount Value",
      width: "10%",
      selector: (row) => row.discountValue,
      center: true,
    },
    // ...conditionalColumns(tab === "2", {
    //     name: "Rejection Reason",
    //     width: "180px",
    //     selector: (row) =>
    //       row.rejectionReason ? capitalizeText(row.rejectionReason) : "-",
    //   }),

    ...conditionalColumns(tab === "2", {
      name: "Rejection Reason",
      width: "150px",
      selector: (row) =>
        row.rejectReason ? capitalizeText(row.rejectReason) : "-",
      cell: (row) => (
        <div
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "150px",
            cursor: row.rejectReason ? "pointer" : "default",
          }}
          title={row.rejectReason}
        >
          {row.rejectReason}
        </div>
      )
    }),
    {
      name: "Status",
      width: "100px",
      selector: (row) => capitalizeText(row.approvalStatus),
    },
    {
      name: "Start Date",
      width: "12%",
      selector: (row) => (row.startDate ? dateConverter(row?.startDate) : "-"),
    },
    {
      name: "End Date",
      width: "12%",

      selector: (row) => (row.endDate ? dateConverter(row?.endDate) : "-"),
    },
    // {
    //   name: "Action",
    //   width: "150px",
    //   cell: actionsCellCallback,
    // },
    ...((!isVendor && canManageOffers) || (isVendor && tab === "0")
      ? [{
        name: "Action",
        width: "150px",
        cell: actionsCellCallback,
      }]
      : []),
  ];

  const getData = useCallback(() => {
    if (tab !== null) {
      dispatch(
        getOffers({
          search: debouncedSearch,
          page: pageSelected,
          limit: rowsPerPageValue,
          approvalStatus: OFFER_STATUS[tab],
        })
      );
    }
  }, [dispatch, debouncedSearch, pageSelected, rowsPerPageValue,tab]);

  const openAddOfferModal = () => {
    dispatch(setModalOpen({ id: MODAL_ID.ADD_OFFER }));
  };

  const handleDelete = (id) => async () => {
      if (!id) return;

    const data = await OfferService.deleteOffer(id);
    if (data) {
      getData();
      toast.success(data?.message || "Offer added successfully");
      dispatch(setModalOpen(false));
    }
  };

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <>
      <RejectionModal handleReject={updateOfferStatus(modalOpen?.data, 2)} />
      <AddOffer getData={getData} />
      <ConfirmDeleteModal
        handleDelete={handleDelete(modalOpen?.data?.id)}
        deleteLabel="offer"
      />
      <div className={styles.main}>
        <CustomHeader
          search={search}
          setSearch={setSearch}
          onButtonClick={checkPermission("add user") && openAddOfferModal}
          buttonLabel="Add"
          showTab={true}
          tabNames={tabNames}
        />
        <Table
          columns={columns}
          data={offers}
          className={styles.candidatesTable}
          progressPending={isFetching}
          paginationProps={{
            isPagination: true,
            tableName: "Accounts",
            pageSelected,
            totalCount: totalOffers,
            rowsPerPageValue,
            setRowsPerPageValue,
            setPageSelected,
          }}
        />
      </div>
    </>
  );
};

export default Offers;
